import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";

interface LazyVideoProps {
  src: string;
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  rootMargin?: string;
}

export const LazyVideo = forwardRef<HTMLVideoElement, LazyVideoProps>(
  ({ src, className, autoPlay = true, loop = true, muted = true, playsInline = true, rootMargin = "200px" }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isInView, setIsInView] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !hasLoaded) {
              setIsInView(true);
              setHasLoaded(true);
              observer.disconnect();
            }
          });
        },
        {
          rootMargin,
          threshold: 0,
        }
      );

      observer.observe(container);

      return () => observer.disconnect();
    }, [hasLoaded, rootMargin]);

    return (
      <div ref={containerRef} className={className}>
        {isInView ? (
          <video
            ref={videoRef}
            src={src}
            autoPlay={autoPlay}
            loop={loop}
            muted={muted}
            playsInline={playsInline}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-100 animate-pulse rounded" />
        )}
      </div>
    );
  }
);

LazyVideo.displayName = "LazyVideo";
